import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import { TallyFormConfiguration } from "@/api/rest/models/configurations";
import {
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useResetTallyFormStatusesForBuilding,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";
import TextArea from "@/components/common/TextArea";
import Checkbox from "@/components/common/Checkbox";

export interface TallyForm {
  language: string;
  url: string;
  bannerText?: string;
}

interface TallyFormSettingsProps {
  initialConfiguration: TallyFormConfiguration;
  buildingId: number;
}

export function TallyFormSettings({ initialConfiguration, buildingId }: TallyFormSettingsProps) {
  const { t } = useTranslation();
  const { mutate: updateConfiguration, isPending: isSaving } = useUpdateConfiguration();
  const { mutate: resetStatuses, isPending: isResetting } = useResetTallyFormStatusesForBuilding();

  const [isEnabled, setIsEnabled] = useState(initialConfiguration.enabled);
  const [forms, setForms] = useState<TallyForm[]>(initialConfiguration.forms);

  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);
  const [resetResult, setResetResult] = useState<ApiResponse<unknown> | null>(null);

  useEffect(() => {
    setIsEnabled(initialConfiguration.enabled);
    setForms(initialConfiguration.forms);
  }, [initialConfiguration]);

  const handleAddForm = () => {
    setForms((prev) => [...prev, { language: "", url: "", bannerText: "" }]);
    setSaveResult(null);
    setResetResult(null);
  };

  const handleRemoveForm = (index: number) => {
    const newForms = forms.filter((_, i) => i !== index);
    setForms(newForms);
    if (newForms.length === 0) {
      setIsEnabled(false);
      handleSave(false, { showBanner: false });
    }
  };

  const handleFormChange = (index: number, field: "language" | "url" | "bannerText", value: string) => {
    const newForms = [...forms];
    newForms[index] = { ...newForms[index], [field]: value };
    setForms(newForms);
    setSaveResult(null);
    setResetResult(null);
  };

  const validateForms = () => {
    for (const form of forms) {
      if (form.language && !/^[a-zA-Z]{2}$/.test(form.language)) {
        setSaveResult({
          success: false,
          message: t("tallyFormSettings.errors.invalidLanguage", { language: form.language }),
          result: undefined,
        });
        return false;
      }

      if (form.url) {
        if (!form.url.includes("tally.so")) {
          setSaveResult({
            success: false,
            message: t("tallyFormSettings.errors.invalidUrl", {
              url: form.url,
            }),
            result: undefined,
          });
          return false;
        }
        if (form.url.endsWith("/edit")) {
          setSaveResult({
            success: false,
            message: t("tallyFormSettings.errors.urlEndsWithEdit"),
            result: undefined,
          });
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = (enabled?: boolean, options?: { showBanner: boolean }) => {
    if (options?.showBanner && !validateForms()) {
      return;
    }
    if (options?.showBanner) {
      setSaveResult(null);
      setResetResult(null);
    }

    const configToSave: TallyFormConfiguration = {
      enabled: enabled ?? isEnabled,
      forms: forms,
    };

    updateConfiguration(
      {
        entityType: EntityConfigurationEntityType.BUILDING,
        buildingId: buildingId,
        configurationType: EntityConfigurationType.TALLY_FORM,
        configurationValue: JSON.stringify(configToSave),
      },
      {
        onSuccess: () => {
          if (options?.showBanner) {
            setSaveResult({ success: true, result: {} });
          }
          // Also update the initial enabled state
          initialConfiguration.enabled = enabled ?? isEnabled;
        },
        onError: (error) => {
          if (options?.showBanner) {
            setSaveResult({
              success: false,
              message: error.message,
              result: undefined,
            });
          }
        },
      },
    );
  };

  const handleResetStatuses = async () => {
    setSaveResult(null);
    setResetResult(null);
    resetStatuses(buildingId, {
      onSuccess: () => {
        setResetResult({ success: true, result: {} });
      },
      onError: (error) => {
        setResetResult({
          success: false,
          message: error.message,
          result: undefined,
        });
      },
    });
  };

  return (
    <div>
      <Checkbox
        label={t("tallyFormSettings.enable")}
        id="tally-enabled"
        checked={isEnabled}
        onChange={(e) => {
          const isChecked = e.target.checked;
          setIsEnabled(isChecked);
          if (isChecked && forms.length === 0) {
            handleAddForm();
          }
          handleSave(isChecked, { showBanner: false });
        }}
      />

      {isEnabled && (
        <div className="mt-4 pl-2">
          {forms.map((form, index) => (
            <div key={index} className="relative mb-4 p-3 border rounded-md bg-slate-50">
              <button
                onClick={() => handleRemoveForm(index)}
                className="absolute top-1 right-1 p-1 text-red-600 hover:text-red-800"
                aria-label={t("tallyFormSettings.removeRule")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <Field id={`tally-lang-${index}`}>
                  <Field.Label>{t("tallyFormSettings.language")}</Field.Label>
                  <Input
                    type="text"
                    value={form.language}
                    onChange={(e) => handleFormChange(index, "language", e.target.value)}
                  />
                </Field>
                <Field id={`tally-url-${index}`}>
                  <Field.Label>{t("tallyFormSettings.url")}</Field.Label>
                  <Input
                    type="text"
                    value={form.url}
                    onChange={(e) => handleFormChange(index, "url", e.target.value)}
                  />
                </Field>
              </div>
              <Field id={`tally-banner-${index}`}>
                <Field.Label>{t("tallyFormSettings.bannerText")}</Field.Label>
                <TextArea
                  value={form.bannerText || ""}
                  onChange={(e) => handleFormChange(index, "bannerText", e.target.value)}
                  rows={2}
                  placeholder={t("tallyFormSettings.bannerTextPlaceholder")}
                />
              </Field>
            </div>
          ))}

          <div className="flex items-center justify-between mt-4 w-full gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleAddForm} className="flex items-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("tallyFormSettings.addForm")}
              </Button>
              <Button variant="danger" onClick={handleResetStatuses} disabled={isResetting} loading={isResetting}>
                {t("tallyFormSettings.resetStatuses")}
              </Button>
              <Button
                onClick={() => handleSave(undefined, { showBanner: true })}
                disabled={isSaving}
                loading={isSaving}
              >
                {isSaving ? t("settings.saving") : t("settings.save")}
              </Button>
            </div>
            <div className="flex-grow">
              {saveResult && (
                <ApiBanner apiResponse={saveResult} successMessage={t("tallyFormSettings.success.saved")} />
              )}
              {resetResult && (
                <ApiBanner apiResponse={resetResult} successMessage={t("tallyFormSettings.success.reset")} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
